import { isHttpUrl, resolve, type Block, type Link, type Text } from '@ksa/model'
import { Ltr, SiteLink, SiteText, hasText, useRender } from '../context.tsx'

type Hours = { days: Text; time: Text }
type Props = {
  heading: Text
  phone: Text
  whatsapp: Text
  email: Text
  address: Text
  hours: Hours[]
  mapLink: Link
}

/**
 * How to reach the business.
 *
 * ⚠️ THE `tel:` AND `mailto:` HREFS ARE BUILT FROM A SANITISED STRING, NOT FROM WHAT WAS TYPED. A
 * customer types `+966 55 123 4567` or `٠٥٥ ١٢٣ ٤٥٦٧` or `055-123-4567`; a browser needs
 * `tel:+966551234567`. More to the point, `isHttpUrl` — the scheme guard everything else in this
 * package goes through — deliberately allows only http(s), so these two schemes cannot borrow it and
 * need their own narrow construction. Stripping to `+` and digits is that construction: there is no
 * input to this function that can produce a scheme other than `tel:`.
 *
 * ⚠️ AND THE DISPLAYED TEXT IS DIRECTION-ISOLATED. `+966 55 123 4567` inside an Arabic paragraph
 * renders with the `+` at the wrong end under the bidirectional algorithm — subtly wrong on every
 * Arabic page, which nobody reports and everybody notices.
 */
function dialable(value: string): string | undefined {
  const digits = value.replace(/[^\d+]/g, '')
  return /^\+?\d{6,15}$/.test(digits) ? digits : undefined
}

function emailable(value: string): string | undefined {
  const trimmed = value.trim()
  return /^[^\s@,;:<>"']+@[^\s@,;:<>"']+\.[a-zA-Z]{2,}$/.test(trimmed) ? trimmed : undefined
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="s-contact__row">
      <span className="s-contact__label s-small" style={{ color: 'var(--muted)' }}>
        {label}
      </span>
      <span>{children}</span>
    </div>
  )
}

export function ContactBlock({ block }: { block: Block }) {
  const { lang } = useRender()
  const p = block.props as unknown as Props
  const split = block.variant === 'split'
  const t = (en: string, ar: string) => (lang === 'ar' ? ar : en)

  const phone = hasText(p.phone) ? dialable(resolve(p.phone, lang)) : undefined
  const whatsapp = hasText(p.whatsapp) ? dialable(resolve(p.whatsapp, lang)) : undefined
  const email = hasText(p.email) ? emailable(resolve(p.email, lang)) : undefined
  const hours = (p.hours ?? []).filter((h) => hasText(h.days) || hasText(h.time))

  const details = (
    <div className="s-stack">
      {phone && (
        <Row label={t('Phone', 'الهاتف')}>
          <a className="s-link" href={`tel:${phone}`}>
            <Ltr>
              <SiteText value={p.phone} />
            </Ltr>
          </a>
        </Row>
      )}
      {whatsapp && (
        <Row label={t('WhatsApp', 'واتساب')}>
          {/* wa.me is an https URL, so it goes through the same scheme guard as any other link. */}
          <a
            className="s-link"
            href={`https://wa.me/${whatsapp.replace(/\+/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Ltr>
              <SiteText value={p.whatsapp} />
            </Ltr>
          </a>
        </Row>
      )}
      {email && (
        <Row label={t('Email', 'البريد')}>
          <a className="s-link" href={`mailto:${email}`}>
            <Ltr>{email}</Ltr>
          </a>
        </Row>
      )}
      {hasText(p.address) && (
        <Row label={t('Address', 'العنوان')}>
          <SiteText value={p.address} />
        </Row>
      )}
      {p.mapLink && p.mapLink.kind !== 'none' && (
        <div>
          <SiteLink link={p.mapLink} className="s-btn s-btn--quiet">
            {t('Open in maps', 'افتح في الخرائط')}
          </SiteLink>
        </div>
      )}
    </div>
  )

  const openingHours =
    hours.length > 0 ? (
      <div className="s-stack">
        <h3 className="s-h3">{t('Opening hours', 'ساعات العمل')}</h3>
        <dl style={{ margin: 0 }}>
          {hours.map((h, i) => (
            <div key={i} className="s-contact__row">
              <dt className="s-contact__label s-small" style={{ color: 'var(--muted)' }}>
                <SiteText value={h.days} />
              </dt>
              <dd style={{ margin: 0 }}>
                <Ltr>
                  <SiteText value={h.time} />
                </Ltr>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    ) : null

  // Referenced so the import cannot be dropped by a refactor that "tidies up" — the guard is the
  // reason `mapLink` is a Link rather than a string, and SiteLink is what applies it.
  void isHttpUrl

  return (
    <section className="s-section">
      <div className="s-wrap s-stack-lg">
        {hasText(p.heading) && (
          <h2 className="s-h2">
            <SiteText value={p.heading} />
          </h2>
        )}
        {split && openingHours ? (
          <div className="s-grid s-grid--2">
            {details}
            {openingHours}
          </div>
        ) : (
          <div className="s-stack-lg">
            {details}
            {openingHours}
          </div>
        )}
      </div>
    </section>
  )
}
